import NiceModal, { NiceModalContext, useModal as useNiceModal } from '@ebay/nice-modal-react'
import { createElement, FC, HTMLAttributes, JSX, memo, useContext, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, type Variants } from 'framer-motion'
import { useMemoizedFn, useTimeout } from 'ahooks'
import { cn } from '@/styles'
import { useHideBodyScroll } from '@/core/useHideBodyScroll'
import { MODAL_NAME } from '@/env'

type Options = {
  /** 預設有一個背景, false = 隱藏 */
  bg?: boolean
  /** 點擊背景關閉 */
  bgClose?: boolean
  /** 客製 className 可以覆蓋化定位方向, Z軸等 */
  bgClassName?: string
  className?: string
  /** 結束事件 */
  onExitComplete?: () => void
  /** 有使用註冊時在使用 */
  name?: string
  /** 預設動畫 */
  variant?: 'scale' | 'fade' | 'slide-top'
  /** 禁止 body 捲動 */
  hideBodyScroll?: boolean
  /** root 包一層 fixed, 預設 true, 注意 false 會無法使用 bgClose */
  fixed?: boolean
}

/** 預設動畫 */
export const ModalVariantMap = new Map<string, Variants>([
  [
    'scale',
    {
      initial: { opacity: 0, scale: 0.5, transition: { duration: 0.3 } },
      animate: { opacity: 1, scale: 1, transition: { ease: 'backOut', duration: 0.3, delay: 0.1 } },
      exit: { opacity: 0, scale: 0.5, transition: { ease: 'easeInOut', duration: 0.2 } },
    },
  ],
  [
    'fade',
    {
      initial: { opacity: 0, transition: { duration: 0.3 } },
      animate: { opacity: 1, transition: { ease: 'backOut', duration: 0.3, delay: 0.1 } },
      exit: { opacity: 0, transition: { ease: 'easeInOut', duration: 0.2 } },
    },
  ],
  [
    'slide-top',
    {
      initial: { opacity: 0, y: '100rem', transition: { duration: 0.3 } },
      animate: { opacity: 1, y: 0, transition: { ease: 'easeInOut', duration: 0.3, delay: 0.1 } },
      exit: { opacity: 0, y: '100rem', transition: { ease: 'easeInOut', duration: 0.2 } },
    },
  ],
])

/** niceModal 彈窗 hooks 封裝 */
export const useModal = (options: Options) => {
  const modal = useNiceModal(options?.name || '')
  const contentRef = useRef<HTMLDivElement>(null)
  const [bgClose, setBgClose] = useState(options?.bgClose)

  /** 防連點 */
  const initial = useRef(Date.now() + 500)
  useEffect(() => {
    return () => {
      initial.current = Date.now() + 500
    }
  }, [modal.visible])

  /** 隱藏捲軸 */
  useHideBodyScroll(options.hideBodyScroll && modal.visible)

  /** 關閉時刪除彈窗 */
  const onExitComplete = useMemoizedFn(() => {
    options?.onExitComplete?.()
    modal.remove()
  })

  /** 動畫失靈補救機制 */
  useTimeout(onExitComplete, modal.visible ? undefined : 1000)

  /** 關閉彈窗並返回承諾 */
  const onModalClose = <T = any,>(meta?: T) => {
    if (Date.now() < initial.current) return // 尚未初始化
    modal?.resolve({ status: 'CLOSE', meta } satisfies ModalResolve)
    modal?.hide()?.catch(console.warn)
  }

  /** 點背景關閉 */
  const onBgClick = useMemoizedFn((e: React.MouseEvent<HTMLDivElement>) => {
    if (Date.now() < initial.current) return // 尚未初始化
    if (!bgClose) return // 未配置背景關閉
    if (!modal.visible) return // 已關閉
    if (e.target !== contentRef?.current) return
    modal?.resolve({ status: 'BG_CLOSE' } satisfies ModalResolve)
    modal?.hide()?.catch(console.warn)
  })

  /** 如果背景可關閉，監聽 ESC */
  useEffect(() => {
    if (!options?.bgClose) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      modal?.resolve({ status: 'BG_CLOSE' } satisfies ModalResolve)
      modal?.hide()?.catch(console.warn)
    }
    document.addEventListener('keydown', onKeyDown, { once: true })
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [options.bgClose])

  return { bgClose, setBgClose, modal, onModalClose, onExitComplete, onBgClick, contentRef }
}

type PropsType = Omit<ReturnType<typeof useModal>, 'onExitComplete' | 'onBgClick'> & {
  variant: Variants
  Dialog: (props: Parameters<typeof motion.div>[0]) => JSX.Element
  CloseButton: (props: HTMLAttributes<HTMLButtonElement>) => JSX.Element
}

/**
 * 封裝 useModal
 * @example
 * const Modal = defineModal(({ Dialog }) => <Dialog />, { ... })
 *
 * // 全域註冊
 * const Modal = dynamic(() => import('path/to/Modal'), { ssr: false })
 * NiceModal.register('DEMO', (props) => <Modal {...props} />) // 請使用 lazy load，避免 chunk 失效
 * NiceModal.show('DEMO', { test: 123 }) // 呼叫彈窗，後方參數為 props
 *
 * // 不註冊的呼叫方式
 * import Modal from 'path/to/Modal'
 * NiceModal.show(Modal, { test: 123 }) // 呼叫彈窗，後方參數為 props
 *
 * @example
 * 接下文時強轉類型
 * const res = await NiceModal.show(Modal) as ModalResolve<TYPE>
 * // res.meta 會是指定的型別
 */
export const defineModal = <TParam = {},>(children: (props: TParam & PropsType) => JSX.Element | null, options: Options = {}) => {
  options.bg = options.bg ?? true
  options.fixed = options.fixed ?? true
  options.bgClose = options.bgClose ?? false
  options.hideBodyScroll = options.hideBodyScroll ?? true
  const variant = ModalVariantMap.get(options?.variant || '') || ModalVariantMap.get('scale')!

  /** 彈窗容器 */
  const Dialog = (props: Parameters<typeof motion.div>[0]) => {
    return (
      <motion.div {...variant} {...props}>
        {props.children}
      </motion.div>
    )
  }

  /** 關閉按鈕：不一定要使用 */
  const BindCloseButton = (onClose: () => void) => {
    const Close = (props?: HTMLAttributes<HTMLButtonElement>) => {
      return (
        <button onClick={onClose} {...props} className={cn('text-muted-foreground hover absolute top-0 right-0 flex h-40 w-40 items-center justify-center p-12', props?.className)}>
          <svg className="h-full w-full" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0.335683 0.335683C0.441822 0.229276 0.56791 0.144854 0.706726 0.0872515C0.845542 0.0296493 0.994358 0 1.14465 0C1.29494 0 1.44376 0.0296493 1.58257 0.0872515C1.72139 0.144854 1.84748 0.229276 1.95362 0.335683L8.0003 6.38465L14.047 0.335683C14.1532 0.229448 14.2793 0.145178 14.4181 0.0876841C14.5569 0.0301901 14.7057 0.000598232 14.8559 0.000598232C15.0062 0.000598232 15.155 0.0301901 15.2938 0.0876841C15.4326 0.145178 15.5587 0.229448 15.6649 0.335683C15.7712 0.441918 15.8554 0.568037 15.9129 0.70684C15.9704 0.845643 16 0.994411 16 1.14465C16 1.29489 15.9704 1.44366 15.9129 1.58246C15.8554 1.72126 15.7712 1.84738 15.6649 1.95362L9.61595 8.0003L15.6649 14.047C15.7712 14.1532 15.8554 14.2793 15.9129 14.4181C15.9704 14.5569 16 14.7057 16 14.8559C16 15.0062 15.9704 15.155 15.9129 15.2938C15.8554 15.4326 15.7712 15.5587 15.6649 15.6649C15.5587 15.7712 15.4326 15.8554 15.2938 15.9129C15.155 15.9704 15.0062 16 14.8559 16C14.7057 16 14.5569 15.9704 14.4181 15.9129C14.2793 15.8554 14.1532 15.7712 14.047 15.6649L8.0003 9.61595L1.95362 15.6649C1.84738 15.7712 1.72126 15.8554 1.58246 15.9129C1.44366 15.9704 1.29489 16 1.14465 16C0.994411 16 0.845643 15.9704 0.70684 15.9129C0.568037 15.8554 0.441918 15.7712 0.335683 15.6649C0.229448 15.5587 0.145178 15.4326 0.0876841 15.2938C0.0301901 15.155 0.000598232 15.0062 0.000598232 14.8559C0.000598232 14.7057 0.0301901 14.5569 0.0876841 14.4181C0.145178 14.2793 0.229448 14.1532 0.335683 14.047L6.38465 8.0003L0.335683 1.95362C0.229276 1.84748 0.144854 1.72139 0.0872515 1.58257C0.0296493 1.44376 0 1.29494 0 1.14465C0 0.994358 0.0296493 0.845542 0.0872515 0.706726C0.144854 0.56791 0.229276 0.441822 0.335683 0.335683Z"
              fill="currentColor"
            />
          </svg>
        </button>
      )
    }
    Close.displayName = 'CloseButton'
    return Close
  }

  return NiceModal.create<TParam & PropsType>((props) => {
    const hooks = useModal(options)
    const { bgClose, setBgClose, modal, onExitComplete, onModalClose, onBgClick, contentRef } = hooks
    const elementProps = { ...props, variant, bgClose, setBgClose, modal, onModalClose, Dialog, CloseButton: BindCloseButton(onModalClose) }

    const CustomContent = options.fixed ? (
      <div
        ref={contentRef}
        onClick={onBgClick}
        className={cn(!options?.className?.includes('override') && 'fixed top-0 left-0 z-200 flex h-full w-full items-center justify-center', options.className)}>
        {createElement<TParam & PropsType>(children, elementProps)}
      </div>
    ) : (
      createElement<TParam & PropsType>(children, elementProps)
    )

    return (
      <AnimatePresence onExitComplete={onExitComplete}>
        {modal.visible && options.bg && (
          <motion.div
            key="bg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5, transition: { delay: 0.05 } }}
            exit={{ opacity: 0 }}
            className={cn('fixed top-0 left-0 z-200 flex h-full w-full items-center justify-center bg-black', options.bgClassName)}
          />
        )}
        {modal.visible && CustomContent}
      </AnimatePresence>
    )
  })
}

/** 顯示狀態直接存到頂層 */
export const ModalState: { [key in keyof typeof MODAL_NAME]?: { visible?: boolean } } = {}

/** 監聽顯示狀態變更 */
export const SubscribeNiceModal = memo(() => {
  const modal = useContext(NiceModalContext)
  Object.assign(ModalState, modal)
  return null
})

SubscribeNiceModal.displayName = 'SubscribeNiceModal'
